import {
  type EntityFilterSchema,
  type FilterState,
  type WorkspaceViewPresentation,
  canonicalFilterState,
  hasActiveFilters,
  normalizeFilterState,
} from '@parcel/filters';
import type { SavedView, SavedViewState } from '@parcel/ui';
import type { Table } from '@tanstack/react-table';

export const PROJECTS_BASE_VIEW_STATE: SavedViewState = {
  columnFilters: [],
  sorting: [{ id: 'detectedAt', desc: true }],
  columnVisibility: {},
  globalFilter: '',
};

/** Account columns off until someone turns them on; the base view carries it, as for contacts. */
export const ACCOUNTS_DEFAULT_HIDDEN: Readonly<Record<string, boolean>> = { kind: false };

export const ACCOUNTS_BASE_VIEW_STATE: SavedViewState = {
  columnFilters: [],
  sorting: [{ id: 'lastActivityAt', desc: true }],
  columnVisibility: { ...ACCOUNTS_DEFAULT_HIDDEN },
  globalFilter: '',
};

/** Contact columns off until someone turns them on; the base view carries it, so a fresh table is not "changed". */
export const CONTACTS_DEFAULT_HIDDEN: Readonly<Record<string, boolean>> = { roles: false };

export const CONTACTS_BASE_VIEW_STATE: SavedViewState = {
  columnFilters: [],
  sorting: [{ id: 'updatedAt', desc: true }],
  columnVisibility: { ...CONTACTS_DEFAULT_HIDDEN },
  globalFilter: '',
};

/** Map legacy SavedViewState presentation fields into the workspace View wire shape. */
export function savedViewStateToPresentation(state: SavedViewState): WorkspaceViewPresentation {
  return {
    version: 1,
    sorting: state.sorting.map((entry) => {
      const item = entry as { id?: unknown; desc?: unknown };
      return {
        id: typeof item.id === 'string' ? item.id : String(item.id ?? ''),
        desc: Boolean(item.desc),
      };
    }),
    column_visibility: { ...state.columnVisibility },
  };
}

export const PROJECTS_BASE_PRESENTATION = savedViewStateToPresentation(PROJECTS_BASE_VIEW_STATE);
export const ACCOUNTS_BASE_PRESENTATION = savedViewStateToPresentation(ACCOUNTS_BASE_VIEW_STATE);
export const CONTACTS_BASE_PRESENTATION = savedViewStateToPresentation(CONTACTS_BASE_VIEW_STATE);

type TableViewApplicator<TData> = Pick<
  Table<TData>,
  'setColumnFilters' | 'setSorting' | 'setColumnVisibility' | 'setGlobalFilter'
>;

export function normalizeSavedViewState(
  state: Partial<Record<keyof SavedViewState, unknown>> | null | undefined,
): SavedViewState {
  const normalized: SavedViewState = {
    columnFilters: Array.isArray(state?.columnFilters) ? state.columnFilters : [],
    sorting: Array.isArray(state?.sorting) ? state.sorting : [],
    columnVisibility:
      state?.columnVisibility && typeof state.columnVisibility === 'object'
        ? (state.columnVisibility as Record<string, boolean>)
        : {},
    globalFilter: typeof state?.globalFilter === 'string' ? state.globalFilter : '',
  };

  if (
    state?.serverFilters &&
    typeof state.serverFilters === 'object' &&
    !Array.isArray(state.serverFilters)
  ) {
    normalized.serverFilters = state.serverFilters as Record<string, unknown>;
  }

  return normalized;
}

export function statesEqual(a: SavedViewState, b: SavedViewState) {
  return JSON.stringify(normalizeSavedViewState(a)) === JSON.stringify(normalizeSavedViewState(b));
}

const FILTER_STATE_VERSION = 1;

/**
 * Fold a registry-keyed FilterState into a SavedViewState under
 * serverFilters[tableId], versioned for forward migration. Empty filter
 * states strip the entry so "no filters" serializes identically everywhere.
 */
export function createFilterSavedViewState(
  tableId: string,
  tableState: SavedViewState,
  filterState: FilterState,
): SavedViewState {
  const normalizedTableState = normalizeSavedViewState(tableState);

  if (!hasActiveFilters(filterState)) {
    const { serverFilters, ...state } = normalizedTableState;
    if (!serverFilters) return state;
    const nextServerFilters = { ...serverFilters };
    delete nextServerFilters[tableId];
    return Object.keys(nextServerFilters).length > 0
      ? { ...state, serverFilters: nextServerFilters }
      : state;
  }

  const canonical = canonicalFilterState(filterState);
  return {
    ...normalizedTableState,
    serverFilters: {
      ...(normalizedTableState.serverFilters ?? {}),
      [tableId]: { v: FILTER_STATE_VERSION, q: canonical.q, fields: canonical.fields },
    },
  };
}

/** Read a stored FilterState back out of a saved view; invalid → empty state. */
export function getSavedFilterState(
  schema: EntityFilterSchema,
  tableId: string,
  state: SavedViewState,
): FilterState {
  const raw = state.serverFilters?.[tableId];
  if (raw == null) return { q: '', fields: {} };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn(`[filters] dropped malformed saved-view filters for ${tableId}`);
    return { q: '', fields: {} };
  }
  const versioned = raw as { v?: unknown };
  if (versioned.v !== FILTER_STATE_VERSION) {
    console.warn(
      `[filters] dropped unsupported saved-view filter payload (v=${String(versioned.v)}) for ${tableId}`,
    );
    return { q: '', fields: {} };
  }
  return normalizeFilterState(schema, raw);
}

export function applySavedViewToTable<TData>(
  table: TableViewApplicator<TData>,
  state: SavedViewState,
) {
  const normalized = normalizeSavedViewState(state);

  table.setColumnFilters(
    normalized.columnFilters as Parameters<TableViewApplicator<TData>['setColumnFilters']>[0],
  );
  table.setSorting(normalized.sorting as Parameters<TableViewApplicator<TData>['setSorting']>[0]);
  table.setColumnVisibility(
    normalized.columnVisibility as Parameters<TableViewApplicator<TData>['setColumnVisibility']>[0],
  );
  table.setGlobalFilter(normalized.globalFilter);
}

export function createSavedView(name: string, state: SavedViewState): SavedView {
  const now = new Date().toISOString();
  const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`;

  return {
    id,
    name,
    state,
    createdAt: now,
    updatedAt: now,
  };
}
